import * as Yup from "yup";

const strongPasswordSchema = Yup.string()
  .min(8, "Password must be at least 8 characters")
  .matches(/[a-z]/, "Password must contain at least one lowercase letter")
  .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
  .matches(/[0-9]/, "Password must contain at least one number")
  .matches(/[!@#$%^&*(),.?":{}|<>]/, "Password must contain at least one special character")
  .required("Password is required");

export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string().required("Password is required"),
});

export const submerchantLoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().required("Password is required"),
  merchantAccountNumber: Yup.string()
    .matches(/^\d+$/, "Account number must be numeric")
    .required("Merchant Account Number is required"),
});

export const registrationSchema = Yup.object().shape({
  firstname: Yup.string()
    .max(50, "First name can't be longer than 50 characters")
    .required("First name is required"),
  middlename: Yup.string().max(
    50,
    "Middle name can't be longer than 50 characters"
  ),
  lastname: Yup.string()
    .max(50, "Last name can't be longer than 50 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: strongPasswordSchema,
  location: Yup.string().required("Location is required"),
  mobileNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must be numeric")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number can't be longer than 15 digits")
    .required("Phone number is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

export const resetPasswordSchema = Yup.object({
  password: strongPasswordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

export const sendEmailSchema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
});

export const addBusinessValidationSchema = Yup.object({
  businessName: Yup.string().required("Business name is required"),
  businessCategoryIds: Yup.array().min(1, "At least one category is required"),
  openingHourFrom: Yup.string().required("Opening hour (from) is required"),
  openingHourTo: Yup.string().required("Opening hour (to) is required"),
}).test(
  'is-valid-range',
  'Opening hour (from) must be before opening hour (to)',
  function (values) {
    if (!values.openingHourFrom || !values.openingHourTo) return true;
    return values.openingHourFrom < values.openingHourTo;
  }
);

export const addProductSchema = Yup.object().shape({
  businessId: Yup.string().required("Business is required"),
  productCategoryId: Yup.string().required("Product category is required"),
  productName: Yup.string()
    .min(2, "Product name should be at least 2 characters")
    .required("Product name is required"),
  description: Yup.string()
    .min(10, "Description should be at least 10 characters")
    .required("Description is required"),
  price: Yup.number()
    .typeError("Price must be a number")
    .positive("Price must be greater than zero")
    .required("Price is required"),
  // stockQuantity: Yup.number()
  //   .typeError("Stock quantity must be a number")
  //   .integer("Stock quantity must be an integer")
  //   .min(0, "Stock quantity must be at least 0")
  //   .required("Stock quantity is required"),
  images: Yup.array().min(1, "At least one product image is required"),
});

export const eventSchema = Yup.object({
  businessId: Yup.string().required("Business is required"),
  eventCategoryId: Yup.string().required("Event category is required"),
  title: Yup.string().required("Event title is required"),
  description: Yup.string().required("Event description is required"),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .min(Yup.ref("startDate"), "End date cannot be before start date")
    .required("End date is required"),
  address: Yup.string().required("Venue address is required"),
  venue: Yup.string().required("Venue is required"),
  capacity: Yup.number()
    .required("Capacity is required")
    .positive("Capacity must be a positive number"),
  eventFormat: Yup.string().required("Event format is required"),
  time: Yup.string().required("Event time is required"),
  tickets: Yup.array()
    .of(
      Yup.object().shape({
        eventTicketTypesId: Yup.string().required(
          "Sitting arrangement is required"
        ),
        eventTicketNamesId: Yup.string().required("Ticket type is required"),
        // "Individual" | "Table" — set by the form when seating is selected
        seatingType: Yup.string().nullable(),
        price: Yup.number()
          .required("Ticket price is required")
          .positive("Price must be a positive number"),
        // UI uses availableTickets for both ticket count and table count
        availableTickets: Yup.number()
          .required("Quantity is required")
          .positive("Quantity must be a positive number"),
        seatsPerTable: Yup.number().when("seatingType", {
          is: "Table",
          then: (schema) =>
            schema
              .required("Seats per table is required")
              .positive("Seats per table must be positive"),
          otherwise: (schema) => schema.notRequired().nullable(),
        }),
      })
    )
    .min(1, "At least one ticket type is required"),
  image: Yup.mixed().required("Event image is required"),
});

export const addSubmerchantSchema = Yup.object().shape({
  firstname: Yup.string()
    .max(50, "First name can't be longer than 50 characters")
    .required("First name is required"),
  middlename: Yup.string().max(
    50,
    "Middle name can't be longer than 50 characters"
  ),
  lastname: Yup.string()
    .max(50, "Last name can't be longer than 50 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: strongPasswordSchema,
  mobileNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must be numeric")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number can't be longer than 15 digits")
    .required("Phone number is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Please confirm your password"),
});

export const editSubmerchantSchema = Yup.object().shape({
  firstname: Yup.string()
    .max(50, "First name can't be longer than 50 characters")
    .required("First name is required"),
  middlename: Yup.string().max(
    50,
    "Middle name can't be longer than 50 characters"
  ),
  lastname: Yup.string()
    .max(50, "Last name can't be longer than 50 characters")
    .required("Last name is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  mobileNumber: Yup.string()
    .matches(/^[0-9]+$/, "Phone number must be numeric")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number can't be longer than 15 digits")
    .required("Phone number is required"),
});

export const kycFormSchema = Yup.object().shape({
  dateOfBirth: Yup.string().required("Date of Birth is required"),
  nationality: Yup.string().required("Nationality is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  postalCode: Yup.string().required("Postal Code is required"),
  country: Yup.string().required("Country is required"),
  idType: Yup.string().required("ID Type is required"),
  idNumber: Yup.string().required("ID Number is required"),
  idExpiryDate: Yup.string().required("ID Expiry Date is required"),
  idFrontImage: Yup.mixed().required("ID Front Image is required"),
  idBackImage: Yup.mixed().required("ID Back Image is required"),
  selfieWithID: Yup.mixed().required("Selfie with ID is required"),
});

export const kybFormSchema = Yup.object().shape({
  industry: Yup.string().required('Industry is required'),
  industryOther: Yup.string().when("industry", {
    is: "Other",
    then: (schema) => schema.trim().required("Please enter your industry"),
    otherwise: (schema) => schema.notRequired(),
  }),
  taxId: Yup.string().required('Tax ID is required'),
  registrationNumber: Yup.string().required('Registration number is required'),
  businessAddress: Yup.string().required('Business address is required'),
  businessCity: Yup.string().required('City is required'),
  businessCountry: Yup.string().required('Country is required'),
  // directors: Yup.array().of(
  //   Yup.object().shape({
  //     firstName: Yup.string().required('First name is required'),
  //     lastName: Yup.string().required('Last name is required'),
  //     phoneNumber: Yup.string().required('Phone number is required'),
  //   })
  // ).min(1, 'At least one director is required'),
  registrationCertificate: Yup.mixed().required('Registration certificate is required'),
  taxClearanceCertificate: Yup.mixed().required('Tax clearance certificate is required'),
  proofOfBusinessAddress: Yup.mixed().required('Proof of address is required'),
});

// Role and Permission validation schemas
export const createRoleSchema = Yup.object().shape({
  roleName: Yup.string().required('Role name is required'),
  roleDescription: Yup.string().required('Role description is required'),
});

export const editRoleSchema = Yup.object().shape({
  roleName: Yup.string().required('Role name is required'),
  roleDescription: Yup.string().required('Role description is required'),
});

export const createPermissionSchema = Yup.object().shape({
  permissionName: Yup.string().required('Permission name is required'),
  permissionDescription: Yup.string().required('Permission description is required'),
});

export const editPermissionSchema = Yup.object().shape({
  permissionName: Yup.string().required('Permission name is required'),
  permissionDescription: Yup.string().required('Permission description is required'),
});

export const changePasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required("Current password is required"),
  newPassword: strongPasswordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Please confirm your new password"),
});
